/**
 * DisputedEyeModal — row detail modal (§11.3).
 *
 * Block M1a-FE (TZ 2026-07-29). Показывает:
 *   - identity (product name)
 *   - raw values (selected_input_columns + любые raw до 10)
 *   - enriched_data facts (если Search layer есть)
 *   - all proposed category paths с score
 *   - per-candidate «Select» button → useResolveRow
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Modal } from '@/components/ui/Modal';
import {
  useResolveRow,
  type MatchingCategoryCandidate,
} from '@/hooks/useMatching';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import type { DisputedCardData } from './DisputedCard';

const RAW_MAX = 10;
const CANDIDATES_MAX = 5;

export interface DisputedEyeModalProps {
  data: DisputedCardData | null;
  datasetId: string | null;
  onClose: () => void;
}

function useRowDetail(rowId: string | null) {
  return useQuery({
    queryKey: ['content', 'row', rowId],
    queryFn: async () => {
      const res = await api.get<{
        row: {
          raw_data: Record<string, unknown> | null;
          enriched_data?: Record<string, unknown> | null;
        };
      }>(`/content/row/${rowId}`);
      return res.data;
    },
    enabled: Boolean(rowId),
    staleTime: 30_000,
  });
}

export function DisputedEyeModal({
  data,
  datasetId,
  onClose,
}: DisputedEyeModalProps) {
  const { t } = useTranslation();
  const [showAllRaw, setShowAllRaw] = useState(false);
  const [showAllCandidates, setShowAllCandidates] = useState(false);
  const resolveRow = useResolveRow();
  const { data: rowResp } = useRowDetail(data?.row_id ?? null);

  const rawEntries = useMemo<Array<[string, string]>>(() => {
    const raw = rowResp?.row.raw_data ?? null;
    if (!raw) return [];
    return Object.entries(raw)
      .filter(([, v]) => v !== null && v !== undefined && v !== '')
      .slice(0, RAW_MAX)
      .map(
        ([k, v]) => [k, typeof v === 'string' ? v : JSON.stringify(v)] as [string, string],
      );
  }, [rowResp]);

  const rawOverflow = useMemo(() => {
    const raw = rowResp?.row.raw_data ?? null;
    if (!raw) return 0;
    return Math.max(0, Object.keys(raw).length - RAW_MAX);
  }, [rowResp]);

  const enriched = rowResp?.row?.enriched_data ?? null;
  const candidates = (data?.candidates ?? [])
    .slice()
    .sort((a, b) => b.score - a.score);

  function handleSelect(c: MatchingCategoryCandidate) {
    if (!data || !datasetId) return;
    resolveRow.mutate(
      {
        dataset_id: datasetId,
        row_id: data.row_id,
        category: { ...c, status: 'confirmed' },
      },
      { onSettled: () => onClose() },
    );
  }

  return (
    <Modal
      open={Boolean(data)}
      onClose={onClose}
      title={t('process.matching.results.disputed.eye.title', {
        defaultValue: 'Row details',
      })}
      maxWidth="xl"
    >
      {!data ? null : (
        <div className="space-y-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-wider text-surface-500">
              {t('process.matching.results.disputed.eye.sectionIdentity', {
                defaultValue: 'Identity',
              })}
            </div>
            <div className="mt-1 font-medium text-surface-100 truncate">
              {data.displayName || '—'}
            </div>
            <div className="text-[11px] font-mono text-surface-500">
              row_id: {data.row_id.slice(0, 8)}…
            </div>
          </div>

          {rawEntries.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wider text-surface-500">
                {t('process.matching.results.disputed.eye.sectionRaw', {
                  defaultValue: 'Source values',
                })}
              </div>
              <dl className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                {rawEntries.map(([k, v]) => (
                  <div key={k} className="flex items-baseline gap-2 min-w-0">
                    <dt className="font-mono text-surface-500 shrink-0">{k}</dt>
                    <dd className="text-surface-200 truncate" title={v}>
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
              {rawOverflow > 0 && (
                <button
                  type="button"
                  onClick={() => setShowAllRaw((v) => !v)}
                  className="mt-1 text-[11px] text-primary-300 hover:text-primary-200"
                >
                  {showAllRaw
                    ? '—'
                    : t('process.matching.results.disputed.eye.rawMore', {
                        defaultValue: '+{n} more columns',
                        n: rawOverflow,
                      })}
                </button>
              )}
            </div>
          )}

          {enriched && (
            <div>
              <div className="text-xs uppercase tracking-wider text-surface-500">
                {t('process.matching.results.disputed.eye.sectionEnriched', {
                  defaultValue: 'Search facts',
                })}
              </div>
              <pre className="mt-1 text-[11px] bg-surface-900 rounded p-2 max-h-40 overflow-auto font-mono text-surface-300">
                {JSON.stringify(enriched, null, 2)}
              </pre>
            </div>
          )}
          {data && enriched === null && (
            <p className="text-xs text-surface-500 italic">
              {t('process.matching.results.disputed.eye.noEnriched', {
                defaultValue: 'No search enrichment for this row.',
              })}
            </p>
          )}

          <div>
            <div className="text-xs uppercase tracking-wider text-surface-500">
              {t('process.matching.results.disputed.eye.sectionCandidates', {
                defaultValue: 'Proposed categories',
              })}
            </div>
            <ul className="mt-2 space-y-2">
              {(showAllCandidates ? candidates : candidates.slice(0, CANDIDATES_MAX)).map(
                (c, i) => (
                  <li
                    key={`${c.category_id}-${i}`}
                    className={cn(
                      'flex items-start justify-between gap-2 px-3 py-2 rounded-md border text-xs',
                      'border-surface-700 bg-surface-900',
                    )}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-surface-100 truncate">
                        {(c.path_ua?.length ? c.path_ua : c.path_ru ?? []).join(' › ') ||
                          c.category_id}
                      </div>
                      <div className="text-[11px] text-surface-500 font-mono">
                        score {Math.round(c.score * 100)}% · {c.status}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSelect(c)}
                      disabled={resolveRow.isPending || !datasetId}
                      className={cn(
                        'shrink-0 px-2.5 py-1 rounded-md text-[11px] transition-colors',
                        resolveRow.isPending || !datasetId
                          ? 'bg-surface-800 text-surface-500 cursor-not-allowed'
                          : 'bg-primary-600 hover:bg-primary-500 text-white',
                      )}
                    >
                      {t('process.matching.results.disputed.eye.select', {
                        defaultValue: 'Select this category',
                      })}
                    </button>
                  </li>
                ),
              )}
            </ul>
            {candidates.length > CANDIDATES_MAX && (
              <button
                type="button"
                onClick={() => setShowAllCandidates((v) => !v)}
                className="mt-1 text-[11px] text-primary-300 hover:text-primary-200"
              >
                {showAllCandidates
                  ? '—'
                  : t('process.matching.results.disputed.eye.candidatesMore', {
                      defaultValue: '+{n} more candidates',
                      n: candidates.length - CANDIDATES_MAX,
                    })}
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
